/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import styles from './ItemsList.module.scss';
import { ReactComponent as ChatIcon } from 'app/assets/Icons/Chat.svg';
import { CollapsibleSection } from 'app/components/Sections/AskYourDocs/Sidebar/CollapsibleSection/CollapsibleSection';
import { OptionsButton } from 'app/components/Sections/AskYourDocs/Sidebar/OptionsButton/OptionsButton';
import { deleteChat, getHistory, renameChat } from 'integration';
import config from 'config';
import { useNavigate } from 'react-router-dom';
import { IChatList, IChatTitles } from './ChatList.interface';
import classnames from 'classnames';
import { InlineEditor } from '../InlineEditor/InlineEditor';
import { Tooltip } from 'react-tooltip';
import { useAppSelector, getService, getBusinessArea } from 'app/redux';
import { SERVICE_NAMES } from 'app/utils/consts';

export const ChatList: React.FC<IChatList> = ({
  onChatSelect,
  selectedChatId,
  refreshChatList,
  chatData,
  setChatData
}) => {
  const navigate = useNavigate();
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const maxDisplayLength = 30;
  const currentService =
    useAppSelector(getService) ?? SERVICE_NAMES.ASK_YOUR_DOCS;
  const currentBusinessArea =
    useAppSelector(getBusinessArea) ?? 'cross-functional';

  useEffect(() => {
    if (refreshChatList && refreshChatList > 0) {
      handleGetChatTitles();
    }
  }, [refreshChatList]);

  useEffect(() => {
    const chatExists = chatData.chats.some(
      (chat) => chat.chatId === selectedChatId
    );
    if (!chatExists) {
      onChatSelect(null);
    }
  }, [chatData, onChatSelect, selectedChatId]);

  const handleGetChatTitles = (): void => {
    setIsLoading(true);
    getHistory(currentBusinessArea, currentService, 'chats')
      .then((response: IChatTitles) => {
        setChatData(response);
      })
      .catch((error) => {
        console.error('Error:', error);
        navigate(config.APP.PATH.LANDING);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteChat = (chatId: string): void => {
    setIsLoading(true);
    deleteChat(currentBusinessArea, currentService, chatId)
      .then(() => {
        const newChatData = {
          ...chatData,
          chats: chatData.chats.filter((chat) => chat.chatId !== chatId)
        };
        setChatData(newChatData);
        if (selectedChatId === chatId) {
          onChatSelect(null);
        }
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRenameChat = (chatId: string, newChatName: string): void => {
    renameChat(currentBusinessArea, currentService, chatId, newChatName)
      .then(() => {
        const newChatData = {
          ...chatData,
          chats: chatData.chats.map((chat) =>
            chat.chatId === chatId ? { ...chat, title: newChatName } : chat
          )
        };
        setChatData(newChatData);
        setEditingChatId(null);
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  const startEditing = (chatId: string): void => {
    setEditingChatId(chatId);
  };

  const cancelEditing = (): void => {
    setEditingChatId(null);
  };

  return (
    <CollapsibleSection title="Chats" icon={<ChatIcon />} isLoading={isLoading}>
      <ul className={styles.list}>
        {chatData.chats.map((chat) => {
          const shouldShowTooltip = chat.title.length > maxDisplayLength;

          return (
            <li key={chat.chatId} className={styles['chat-item-wrapper']}>
              <button
                type="button"
                className={classnames(styles['chat-item'], {
                  [styles.selected]: selectedChatId === chat.chatId
                })}
                onClick={() => onChatSelect(chat.chatId)}
              >
                {editingChatId === chat.chatId ? (
                  <InlineEditor
                    value={chat.title}
                    onSave={(newName) => handleRenameChat(chat.chatId, newName)}
                    onCancel={cancelEditing}
                    placeholder={chat.title}
                  />
                ) : (
                  <span
                    className={classnames({
                      [styles['shortened-title']]: shouldShowTooltip
                    })}
                    {...(shouldShowTooltip && {
                      'data-tooltip-id': 'chat-title-tooltip',
                      'data-tooltip-content': chat.title
                    })}
                  >
                    {shouldShowTooltip
                      ? `${chat.title.slice(0, maxDisplayLength)}...`
                      : chat.title}
                  </span>
                )}

                <OptionsButton
                  variant="chat"
                  onEdit={() => startEditing(chat.chatId)}
                  onDelete={() => handleDeleteChat(chat.chatId)}
                />
              </button>
            </li>
          );
        })}
      </ul>
      <Tooltip
        id="chat-title-tooltip"
        className={styles['shortened-title-tooltip']}
        noArrow
        opacity={1}
      />
    </CollapsibleSection>
  );
};
